// modals.js
'use strict';
import { fromJS } from 'immutable';
import {
  ADD_MODAL,
  ADD_MODAL_NEXT_ID,
  REMOVE_MODALS,
  LOGOUT,
  SET_MODAL_STATUS,
  RESTORE_MODALS,
  CLEAR_MODALS,
  // -- PLOP_PREPEND_REDUCER_ACTION_TYPE --
} from '../ActionTypes.js';

export const modalTemplate = {
  id: '',
  prevId: '',
  nextIds: [],
  higherThanIds: [],
  zIndex: 0,
  status: 'display',
  isAlwaysHigher: false,
};

export const defaultState = fromJS({});

/**
 * State of modal views, includes z-index, dependences.
 * @module reducer/modals
 */
const modals = (state = defaultState, action) => {
  switch (action.type) {
    case ADD_MODAL:
      return _ADD_MODAL(action.payload)(state);
    case ADD_MODAL_NEXT_ID:
      return _ADD_MODAL_NEXT_ID(action.payload)(state);
    case REMOVE_MODALS:
      return _REMOVE_MODALS(action.payload)(state);
    case LOGOUT:
      return _LOGOUT(action.payload)(state);
    case SET_MODAL_STATUS:
      return _SET_MODAL_STATUS(action.payload)(state);
    case RESTORE_MODALS:
      return _RESTORE_MODALS(action.payload)(state);
    case CLEAR_MODALS:
      return _CLEAR_MODALS(action.payload)(state);
    // -- PLOP_PREPEND_REDUCER_SWITCH_CASE --
    default:
      return state;
  }
};

/**
 * Add modal
 * @kind reducer/actionType
 * @name ADD_MODAL
 * @param {object} modal - modal to add
 * @param {array} [zIndexUpdates] - existing modal z-index updates
 * @return {Immutable.Map} New state
 */
const _ADD_MODAL =
  ({ modal, zIndexUpdates = [] }) =>
  state => {
    const stateWithModal = state.mergeDeep({
      [modal.id]: { ...modalTemplate, ...modal },
    });

    return zIndexUpdates.reduce(
      (nextState, { id, zIndex }) => nextState.setIn([id, 'zIndex'], zIndex),
      stateWithModal
    );
  };

/**
 * Add modal next id.
 * @kind reducer/actionType
 * @name ADD_MODAL_NEXT_ID
 * @param {string} {id} - modal id
 * @param {string} {nextId} - next modal id
 * @return {Immutable.Map} New state
 */
const _ADD_MODAL_NEXT_ID =
  ({ id, nextId }) =>
  state => {
    return state.updateIn(
      [id, 'nextIds'],
      nextIds => nextIds && nextIds.push(nextId)
    );
  };

/**
 * Remove modals
 * @kind reducer/actionType
 * @name REMOVE_MODALS
 * @param {array} {ids} - modal ids
 * @return {Immutable.Map} New state
 */
const _REMOVE_MODALS =
  ({ ids }) =>
  state => {
    return state.filter(modal => !ids.includes(modal.get('id')));
  };

const persistModalIds = [
  'GeneralUrlPopup',
  'DrmNotSupportAlert',
  'HamburgerMenuModal',
  'LandingPageModal',
];

/**
 * Logout
 * @kind reducer/actionType
 * @name LOGOUT
 * @return {Immutable.Map} New state
 */
const _LOGOUT = () => state => {
  return state.filter(modal => persistModalIds.includes(modal.get('id')));
};

/**
 * Set modal status
 * @kind reducer/actionType
 * @name SET_MODAL_STATUS
 * @param {string} {id} - modal id
 * @param {string} {status} - modal status
 * @return {Immutable.Map} New state
 */
const _SET_MODAL_STATUS =
  ({ id, status }) =>
  state => {
    return state.setIn([id, 'status'], status);
  };

/**
 * Restore modals
 * @kind reducer/actionType
 * @name RESTORE_MODALS
 * @param {object} modalData - modal data
 * @return {Immutable.Map} New state
 */
const _RESTORE_MODALS = modalData => state => {
  return state.mergeDeep(modalData);
};

/**
 * Clear Modals
 * @kind reducer/actionType
 * @name CLEAR_MODALS
 * @return {Immutable.Map} New state
 */
const _CLEAR_MODALS = () => () => {
  return defaultState;
};

// -- PLOP_PREPEND_REDUCER_ACTION_HANDLER --

export default modals;
